import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

/* =====================================================================
 * AdminShopify — Painel administrativo de integrações Shopify
 *
 * Mostra para o ADMIN:
 *  - KPIs globais (integrações, faturamento, ticket médio, itens vendidos)
 *  - Top 5 lojas por faturamento
 *  - Distribuição de status de sync
 *  - Tabela completa de clientes integrados com busca/ordenação/paginação
 *  - Modal de detalhes (produtos top, pedidos, configurações da loja)
 *
 * Endpoints: /api/admin/shopify/{overview,clients,clients/:userId}
 * =================================================================== */

const fmtMoney = (n, currency = 'BRL') => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: (currency || 'BRL').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(v);
  } catch (_) {
    return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};
const fmtNum = (n) => (Number(n) || 0).toLocaleString('pt-BR');
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};
const fmtRelative = (s) => {
  if (!s) return '—';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '—';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d atrás`;
  return d.toLocaleDateString('pt-BR');
};

const AdminShopify = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('revenue');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Guard admin
  useEffect(() => {
    if (!user?.isAdmin) navigate('/dashboard');
  }, [user, navigate]);

  /* ---------------- Overview ---------------- */
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const r = await api.get('/admin/shopify/overview');
      setOverview(r.data);
    } catch (err) {
      console.error('[AdminShopify] overview error:', err);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  /* ---------------- Clients ---------------- */
  const fetchClients = useCallback(async (page = 1) => {
    setClientsLoading(true);
    try {
      const r = await api.get('/admin/shopify/clients', {
        params: { search, sort, page, pageSize: pagination.pageSize },
      });
      setClients(r.data.clients || []);
      setPagination(r.data.pagination || pagination);
    } catch (err) {
      console.error('[AdminShopify] clients error:', err);
    } finally {
      setClientsLoading(false);
    }
  }, [search, sort, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchClients(1); }, [fetchClients]);

  /* ---------------- Detail ---------------- */
  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetailError('');
    setDetail({ loading: true, userId });
    try {
      const r = await api.get(`/admin/shopify/clients/${userId}`);
      setDetail(r.data);
    } catch (err) {
      console.error('[AdminShopify] detail error:', err);
      setDetailError(err.response?.data?.error || 'Erro ao buscar detalhes');
      setDetail({ error: true, userId });
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetail(null); setDetailError(''); };

  /* ---------------- Derived ---------------- */
  const sortedDailySeries = useMemo(() => {
    if (!overview?.dailySeries) return [];
    return overview.dailySeries.slice(-14); // últimas 2 semanas
  }, [overview]);
  const maxRevDay = useMemo(() => {
    if (!sortedDailySeries.length) return 0;
    return Math.max(...sortedDailySeries.map((d) => Number(d.revenue) || 0));
  }, [sortedDailySeries]);

  /* =================================================================== */
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{styles}</style>

      <div className="ash">
        {/* HEADER */}
        <header className="ash-header">
          <div className="ash-header-inner">
            <div className="ash-header-left">
              {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
              <div>
                <h1 className="ash-title">
                  <span className="ash-logo"><i className="fab fa-shopify"></i></span>
                  Integrações Shopify
                </h1>
                <p className="ash-subtitle">Visão consolidada de todas as lojas conectadas pelos clientes</p>
              </div>
            </div>
            <div className="ash-header-right">
              <button className="ash-btn-ghost" onClick={() => { fetchOverview(); fetchClients(pagination.page); }}>
                <i className="fas fa-arrows-rotate"></i> Atualizar
              </button>
            </div>
          </div>
        </header>

        <main className="ash-main">
          {/* OVERVIEW KPIs */}
          {overviewLoading || !overview ? (
            <div className="ash-skeleton-kpis">
              {[...Array(4)].map((_, i) => <div key={i} className="ash-skel" />)}
            </div>
          ) : (
            <>
              <section className="ash-kpis">
                <KpiCard
                  icon="fa-store"
                  iconBg="green"
                  label="Lojas conectadas"
                  value={fmtNum(overview.totals.integrations)}
                  hint={`${fmtNum(overview.totals.activeIntegrations)} ativas`}
                />
                <KpiCard
                  icon="fa-coins"
                  iconBg="orange"
                  label="Faturamento total"
                  value={fmtMoney(overview.totals.revenue)}
                  hint={`${fmtNum(overview.totals.ordersDone)} pedidos processados`}
                />
                <KpiCard
                  icon="fa-ticket"
                  iconBg="purple"
                  label="Ticket médio global"
                  value={fmtMoney(overview.totals.avgTicket)}
                  hint={overview.totals.ordersDone > 0 ? 'Por pedido' : 'Sem dados ainda'}
                />
                <KpiCard
                  icon="fa-box-open"
                  iconBg="blue"
                  label="Itens vendidos"
                  value={fmtNum(overview.totals.itemsTotal)}
                  hint={`${fmtNum(overview.totals.clientsWithOrders)} clientes ativos`}
                />
              </section>

              {/* Gráfico simples + top 5 lojas */}
              <section className="ash-grid-2">
                <div className="ash-card">
                  <div className="ash-card-head">
                    <div className="ash-card-head-left">
                      <div className="ash-card-icon orange"><i className="fas fa-chart-column"></i></div>
                      <div>
                        <h2 className="ash-card-title">Faturamento — últimos 14 dias</h2>
                        <p className="ash-card-sub">Soma de pedidos processados por dia</p>
                      </div>
                    </div>
                  </div>
                  {sortedDailySeries.length === 0 ? (
                    <div className="ash-empty-mini">
                      <i className="fas fa-chart-line"></i>
                      <p>Ainda sem vendas nos últimos 14 dias</p>
                    </div>
                  ) : (
                    <div className="ash-chart">
                      {sortedDailySeries.map((d) => {
                        const h = maxRevDay > 0 ? Math.max(4, Math.round((Number(d.revenue) || 0) / maxRevDay * 120)) : 4;
                        return (
                          <div key={d.day} className="ash-bar-col" title={`${d.day} • ${fmtMoney(d.revenue)} • ${fmtNum(d.orders)} pedido(s)`}>
                            <div className="ash-bar" style={{ height: `${h}px` }}>
                              <span className="ash-bar-val">{Number(d.revenue) > 0 ? fmtMoney(d.revenue).replace('R$', '').trim() : ''}</span>
                            </div>
                            <div className="ash-bar-label">{(d.day || '').slice(5)}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="ash-card">
                  <div className="ash-card-head">
                    <div className="ash-card-head-left">
                      <div className="ash-card-icon green"><i className="fas fa-trophy"></i></div>
                      <div>
                        <h2 className="ash-card-title">Top 5 lojas</h2>
                        <p className="ash-card-sub">Maior faturamento processado</p>
                      </div>
                    </div>
                  </div>
                  {overview.topShops?.length === 0 ? (
                    <div className="ash-empty-mini">
                      <i className="fas fa-store-slash"></i>
                      <p>Nenhuma loja com vendas ainda</p>
                    </div>
                  ) : (
                    <ol className="ash-top-list">
                      {overview.topShops.map((s, i) => (
                        <li key={s.userId} onClick={() => openDetail(s.userId)}>
                          <span className={`ash-top-rank rank-${i + 1}`}>#{i + 1}</span>
                          <div className="ash-top-info">
                            <strong>{s.shopDomain}</strong>
                            <span>{s.username || s.email || `User ${s.userId}`}</span>
                          </div>
                          <div className="ash-top-stats">
                            <strong>{fmtMoney(s.revenue)}</strong>
                            <span>{fmtNum(s.orders)} pedido(s)</span>
                          </div>
                          <i className="fas fa-chevron-right ash-top-arrow"></i>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </section>
            </>
          )}

          {/* TABELA DE CLIENTES */}
          <section className="ash-card">
            <div className="ash-card-head">
              <div className="ash-card-head-left">
                <div className="ash-card-icon orange"><i className="fas fa-users"></i></div>
                <div>
                  <h2 className="ash-card-title">Clientes integrados</h2>
                  <p className="ash-card-sub">{fmtNum(pagination.total)} loja(s) conectada(s)</p>
                </div>
              </div>
              <div className="ash-toolbar">
                <div className="ash-search">
                  <i className="fas fa-magnifying-glass"></i>
                  <input
                    type="text"
                    placeholder="Buscar por domínio, usuário ou e-mail…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select className="ash-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="revenue">Maior faturamento</option>
                  <option value="orders">Mais pedidos</option>
                  <option value="recent">Sync mais recente</option>
                  <option value="domain">Domínio (A-Z)</option>
                </select>
              </div>
            </div>

            {clientsLoading ? (
              <div className="ash-loading">
                <div className="ash-spinner"></div>
                <p>Carregando clientes…</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="ash-empty">
                <i className="fas fa-store-slash"></i>
                <h3>Nenhum cliente integrado</h3>
                <p>Quando um usuário conectar uma loja Shopify, ela aparecerá aqui.</p>
              </div>
            ) : (
              <>
                <div className="ash-table-wrap">
                  <table className="ash-table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th className="num">Lojas</th>
                        <th className="num">Pedidos</th>
                        <th className="num">Itens</th>
                        <th className="num">Ticket médio</th>
                        <th className="num">Faturamento</th>
                        <th>Última sync</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((c) => (
                        <tr key={c.userId} onClick={() => openDetail(c.userId)}>
                          <td data-label="Cliente">
                            <div className="ash-cell-shop">
                              <span className="ash-cell-icon"><i className="fab fa-shopify"></i></span>
                              <div>
                                <strong>{c.username || `Usuário #${c.userId}`}</strong>
                                <span>{c.email || '—'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="num" data-label="Lojas">
                            <span className="ash-tag orange">{c.stores} {c.stores === 1 ? 'loja' : 'lojas'}</span>
                          </td>
                          <td className="num" data-label="Pedidos">
                            <div className="ash-cell-orders">
                              <strong>{fmtNum(c.orders)}</strong>
                              <small>
                                {c.ordersSuccess > 0 && <span className="ash-mini-pill green">{c.ordersSuccess} ✓</span>}
                                {c.ordersPartial > 0 && <span className="ash-mini-pill yellow">{c.ordersPartial} ⚠</span>}
                                {c.ordersFailed > 0 && <span className="ash-mini-pill red">{c.ordersFailed} ✕</span>}
                              </small>
                            </div>
                          </td>
                          <td className="num" data-label="Itens">{fmtNum(c.itemsTotal)}</td>
                          <td className="num" data-label="Ticket médio">{fmtMoney(c.avgTicket, c.currency)}</td>
                          <td className="num" data-label="Faturamento"><strong>{fmtMoney(c.revenue, c.currency)}</strong></td>
                          <td data-label="Última sync">
                            <div className="ash-cell-time">
                              <strong>{fmtRelative(c.lastSyncAt)}</strong>
                            </div>
                          </td>
                          <td className="num" data-label="Ações">
                            <button className="ash-btn-icon" title="Ver detalhes">
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}
                {pagination.totalPages > 1 && (
                  <div className="ash-pagination">
                    <button
                      onClick={() => fetchClients(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page <= 1}
                    >
                      <i className="fas fa-chevron-left"></i> Anterior
                    </button>
                    <span>Página {pagination.page} de {pagination.totalPages}</span>
                    <button
                      onClick={() => fetchClients(Math.min(pagination.totalPages, pagination.page + 1))}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Próxima <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        {/* DRAWER DE DETALHES */}
        {detail && (
          <DetailDrawer
            data={detail}
            loading={detailLoading}
            error={detailError}
            onClose={closeDetail}
            navigate={navigate}
          />
        )}
      </div>
    </>
  );
};

/* ===== Sub-componentes ===== */
const KpiCard = ({ icon, iconBg, label, value, hint }) => (
  <div className="ash-kpi">
    <div className={`ash-kpi-icon ${iconBg}`}><i className={`fas ${icon}`}></i></div>
    <div className="ash-kpi-body">
      <div className="ash-kpi-label">{label}</div>
      <div className="ash-kpi-value">{value}</div>
      {hint && <div className="ash-kpi-hint">{hint}</div>}
    </div>
  </div>
);

const DetailDrawer = ({ data, loading, error, onClose, navigate }) => {
  const isReady = data && !data.loading && !data.error && Array.isArray(data.stores) && data.stores.length > 0;
  const [activeStoreIdx, setActiveStoreIdx] = useState(0);
  const store = isReady ? data.stores[Math.min(activeStoreIdx, data.stores.length - 1)] : null;

  return (
    <div className="ash-drawer-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="ash-drawer">
        <header className="ash-drawer-head">
          {isReady ? (
            <>
              <div className="ash-drawer-title">
                <span className="ash-drawer-icon"><i className="fab fa-shopify"></i></span>
                <div>
                  <strong>{data.user.username || `Usuário #${data.user.userId}`}</strong>
                  <span>
                    {data.user.email || '—'} • {data.storeCount} {data.storeCount === 1 ? 'loja' : 'lojas'}
                  </span>
                </div>
              </div>
              <div className="ash-drawer-actions">
                <button className="ash-btn-ghost" onClick={() => navigate(`/admin/users/${store.integration.userId || data.user.userId}`)} title="Ver usuário">
                  <i className="fas fa-user"></i> Usuário
                </button>
                <button className="ash-drawer-close" onClick={onClose}><i className="fas fa-times"></i></button>
              </div>
            </>
          ) : (
            <>
              <div className="ash-drawer-title">
                <span className="ash-drawer-icon"><i className="fab fa-shopify"></i></span>
                <div><strong>Detalhes do cliente</strong><span>Carregando…</span></div>
              </div>
              <button className="ash-drawer-close" onClick={onClose}><i className="fas fa-times"></i></button>
            </>
          )}
        </header>

        <div className="ash-drawer-body">
          {loading || data?.loading ? (
            <div className="ash-loading"><div className="ash-spinner"></div><p>Buscando dados…</p></div>
          ) : error || data?.error ? (
            <div className="ash-alert error"><i className="fas fa-circle-exclamation"></i> {error || 'Erro ao carregar'}</div>
          ) : isReady ? (
            <>
              {/* TOTAIS AGREGADOS (todas as lojas do cliente) */}
              <div className="ash-metrics">
                <Metric label="Pedidos (total)" value={fmtNum(data.totals.orders)} />
                <Metric label="Faturamento (total)" value={fmtMoney(data.totals.revenue)} highlight />
                <Metric label="Itens (total)" value={fmtNum(data.totals.itemsTotal)} />
                <Metric label="Créditos da conta" value={fmtNum(data.user.credits)} />
              </div>

              {/* SELETOR DE LOJA (quando há mais de 1) */}
              {data.stores.length > 1 && (
                <div className="ash-store-tabs">
                  {data.stores.map((s, idx) => (
                    <button
                      key={s.integration.integrationId}
                      className={`ash-store-tab ${idx === activeStoreIdx ? 'is-active' : ''}`}
                      onClick={() => setActiveStoreIdx(idx)}
                    >
                      <i className="fab fa-shopify"></i> {s.integration.storeName || s.integration.shopDomain}
                    </button>
                  ))}
                </div>
              )}

              {/* MÉTRICAS DA LOJA SELECIONADA */}
              <div className="ash-metrics">
                <Metric label="Pedidos" value={fmtNum(store.metrics.orders)} />
                <Metric label="Faturamento" value={fmtMoney(store.metrics.revenue, store.metrics.currency)} highlight />
                <Metric label="Ticket médio" value={fmtMoney(store.metrics.avgTicket, store.metrics.currency)} />
                <Metric label="Itens vendidos" value={fmtNum(store.metrics.itemsTotal)} />
              </div>

              {/* STATUS PILLS */}
              <div className="ash-status-row">
                <span className="ash-pill green">
                  <i className="fas fa-check-circle"></i> {fmtNum(store.metrics.ordersSuccess)} sucesso
                </span>
                {store.metrics.ordersPartial > 0 && (
                  <span className="ash-pill yellow">
                    <i className="fas fa-circle-exclamation"></i> {fmtNum(store.metrics.ordersPartial)} parcial
                  </span>
                )}
                {store.metrics.ordersFailed > 0 && (
                  <span className="ash-pill red">
                    <i className="fas fa-times-circle"></i> {fmtNum(store.metrics.ordersFailed)} falha
                  </span>
                )}
              </div>

              {/* CONFIG SHOP */}
              <div className="ash-section">
                <h3><i className="fas fa-gear"></i> Configurações da loja{store.integration.storeName ? ` — ${store.integration.storeName}` : ''}</h3>
                <dl className="ash-config">
                  <div><dt>Domínio</dt><dd><code>{store.integration.shopDomain}</code></dd></div>
                  <div><dt>Origem fixa</dt><dd>{store.integration.defaultOrigin || <em>não definida</em>}</dd></div>
                  <div><dt>Transportadora</dt><dd>{store.integration.trackingCompany || '—'}</dd></div>
                  <div><dt>Rota</dt><dd><span className="ash-tag orange">{store.integration.routeType}</span></dd></div>
                  <div><dt>Auto-fulfill</dt><dd>{store.integration.autoFulfill ? '✅ Ativo' : '❌ Desativado'}</dd></div>
                  <div><dt>Notificar cliente</dt><dd>{store.integration.notifyCustomer ? '✅ Sim' : '❌ Não'}</dd></div>
                  <div><dt>URL de rastreio</dt><dd className="ash-config-url">{store.integration.trackingUrlTemplate || '—'}</dd></div>
                  <div><dt>Conectada em</dt><dd>{fmtDate(store.integration.connectedAt)}</dd></div>
                  <div><dt>Última sync</dt><dd>{fmtDate(store.integration.lastSyncAt)}</dd></div>
                </dl>
              </div>

              {/* TOP PRODUTOS */}
              <div className="ash-section">
                <h3><i className="fas fa-box"></i> Produtos mais vendidos</h3>
                {store.topProducts.length === 0 ? (
                  <div className="ash-empty-mini"><i className="fas fa-box-open"></i><p>Ainda sem produtos sincronizados</p></div>
                ) : (
                  <div className="ash-table-wrap">
                    <table className="ash-table compact">
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th>SKU</th>
                          <th className="num">Qtd</th>
                          <th className="num">Pedidos</th>
                          <th className="num">Faturamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {store.topProducts.map((p) => (
                          <tr key={`${p.productId || p.sku || p.title}`}>
                            <td data-label="Produto">
                              <strong>{p.title}</strong>
                            </td>
                            <td data-label="SKU"><code>{p.sku || '—'}</code></td>
                            <td className="num" data-label="Qtd">{fmtNum(p.quantity)}</td>
                            <td className="num" data-label="Pedidos">{fmtNum(p.orders)}</td>
                            <td className="num" data-label="Faturamento"><strong>{fmtMoney(p.revenue, store.metrics.currency)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* PEDIDOS RECENTES */}
              <div className="ash-section">
                <h3><i className="fas fa-cart-shopping"></i> Últimos pedidos ({store.orders.length})</h3>
                {store.orders.length === 0 ? (
                  <div className="ash-empty-mini"><i className="fas fa-inbox"></i><p>Sem pedidos sincronizados</p></div>
                ) : (
                  <div className="ash-orders-list">
                    {store.orders.slice(0, 50).map((o) => (
                      <div key={o.id} className={`ash-order ash-order-${o.status}`}>
                        <div className="ash-order-main">
                          <div className="ash-order-head">
                            <strong>{o.orderName}</strong>
                            <span className={`ash-pill-sm ${o.status === 'success' ? 'green' : o.status === 'partial' ? 'yellow' : 'red'}`}>
                              {o.status}
                            </span>
                            {o.trackingCode && <code className="ash-tracking">{o.trackingCode}</code>}
                          </div>
                          <div className="ash-order-meta">
                            <span><i className="fas fa-user"></i> {o.customerName || '—'}</span>
                            {o.customerEmail && <span><i className="fas fa-envelope"></i> {o.customerEmail}</span>}
                            {o.customerPhone && <span><i className="fas fa-phone"></i> {o.customerPhone}</span>}
                            <span><i className="fas fa-box"></i> {fmtNum(o.itemsCount)} itens</span>
                            <span><i className="fas fa-calendar"></i> {fmtRelative(o.syncedAt)}</span>
                          </div>
                          {o.destination && (
                            <div className="ash-order-dest">
                              <i className="fas fa-location-dot"></i> {o.destination}
                            </div>
                          )}
                          {o.errorMessage && (
                            <div className="ash-order-error">
                              <i className="fas fa-circle-exclamation"></i> {o.errorMessage}
                            </div>
                          )}
                        </div>
                        <div className="ash-order-side">
                          <strong>{fmtMoney(o.orderTotal, o.currency)}</strong>
                          {o.currency && <small>{o.currency}</small>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
};

const Metric = ({ label, value, highlight }) => (
  <div className={`ash-metric ${highlight ? 'highlight' : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

/* ===================================================================
 * STYLES
 * =================================================================== */
const styles = `
  .ash, .ash *, .ash *::before, .ash *::after { margin: 0; padding: 0; box-sizing: border-box; }
  .ash {
    font-family: 'Lexend', sans-serif;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececef 100%);
    min-height: 100vh; color: #1f2937;
    -webkit-font-smoothing: antialiased; overflow-x: hidden;
  }

  /* HEADER */
  .ash-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 100;
  }
  .ash-header-inner {
    max-width: 1400px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
  }
  .ash-header-left { display: flex; align-items: center; gap: 14px; }
  .ash-back {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.10);
    color: #f90; cursor: pointer; display: flex; align-items: center; justify-content: center;
    font-size: 16px; transition: all .2s;
  }
  .ash-back:hover { background: rgba(255,153,0,0.15); transform: translateX(-2px); }
  .ash-title {
    font-size: 19px; font-weight: 700; color: #fff;
    display: flex; align-items: center; gap: 10px;
  }
  .ash-logo {
    width: 28px; height: 28px; border-radius: 7px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px;
  }
  .ash-subtitle { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .ash-header-right { display: flex; gap: 10px; }

  /* BUTTONS */
  .ash-btn-ghost {
    background: rgba(255,255,255,0.06); color: #fff; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px; padding: 9px 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; gap: 8px;
    font-family: inherit; white-space: nowrap;
  }
  .ash-btn-ghost:hover { background: rgba(255,153,0,0.18); border-color: #f90; color: #fdba74; }
  .ash-btn-icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: #f8f9fa; border: 1px solid #e5e7eb; color: #6b7280;
    cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    transition: all .2s; font-family: inherit;
  }
  .ash-btn-icon:hover { background: #fff5e6; border-color: #f90; color: #f90; }

  /* MAIN */
  .ash-main { max-width: 1400px; margin: 0 auto; padding: 26px 24px 60px; display: grid; gap: 22px; }
  /* min-width: 0 — sem isso, uma tabela larga dentro do card empurra a página
     inteira pra fora da tela (o overflow-x:auto do ash-table-wrap só funciona
     se o card puder encolher). Essencial pra telas estreitas/monitor vertical. */
  .ash-main > *, .ash-grid-2 > *, .ash-drawer-body > * { min-width: 0; }

  /* KPIs */
  .ash-kpis {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
  }
  .ash-skeleton-kpis {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px;
  }
  .ash-skel {
    background: linear-gradient(90deg, #e5e7eb 25%, #f1f5f9 50%, #e5e7eb 75%);
    background-size: 200% 100%;
    animation: ashShimmer 1.4s linear infinite;
    height: 96px; border-radius: 16px;
  }
  @keyframes ashShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  .ash-kpi {
    background: #fff; border: 1px solid rgba(0,0,0,0.04); border-radius: 16px;
    padding: 18px 20px; display: flex; align-items: center; gap: 16px;
    box-shadow: 0 4px 18px rgba(15,23,42,0.05);
    transition: all .25s;
  }
  .ash-kpi:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(15,23,42,0.08); }
  .ash-kpi-icon {
    width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 20px;
  }
  .ash-kpi-icon.orange { background: linear-gradient(135deg, #f90, #ff6600); box-shadow: 0 4px 14px rgba(255,153,0,0.30); }
  .ash-kpi-icon.green { background: linear-gradient(135deg, #95BF47, #5E8E3E); box-shadow: 0 4px 14px rgba(149,191,71,0.30); }
  .ash-kpi-icon.purple { background: linear-gradient(135deg, #a855f7, #7c3aed); box-shadow: 0 4px 14px rgba(168,85,247,0.30); }
  .ash-kpi-icon.blue { background: linear-gradient(135deg, #3b82f6, #1d4ed8); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }
  .ash-kpi-body { flex: 1; min-width: 0; }
  .ash-kpi-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; }
  .ash-kpi-value { font-size: 22px; font-weight: 800; color: #0f172a; margin-top: 3px; letter-spacing: -0.5px; }
  .ash-kpi-hint { font-size: 12px; color: #94a3b8; margin-top: 2px; }

  /* GRID 2 */
  .ash-grid-2 {
    display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px;
  }

  /* CARD */
  .ash-card {
    background: #fff; border: 1px solid rgba(0,0,0,0.04); border-radius: 18px; padding: 22px 24px;
    box-shadow: 0 4px 20px rgba(15,23,42,0.05);
  }
  .ash-card-head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .ash-card-head-left { display: flex; align-items: center; gap: 12px; }
  .ash-card-icon {
    width: 42px; height: 42px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center; color: #fff; font-size: 17px;
    flex-shrink: 0;
  }
  .ash-card-icon.orange { background: linear-gradient(135deg, #f90, #ff6600); box-shadow: 0 4px 12px rgba(255,153,0,0.30); }
  .ash-card-icon.green { background: linear-gradient(135deg, #95BF47, #5E8E3E); box-shadow: 0 4px 12px rgba(149,191,71,0.30); }
  .ash-card-title { font-size: 16px; font-weight: 700; color: #0f172a; }
  .ash-card-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }

  /* CHART */
  .ash-chart {
    display: flex; align-items: flex-end; gap: 8px;
    height: 160px; padding-top: 24px; overflow-x: auto;
  }
  .ash-bar-col {
    flex: 1; min-width: 28px; display: flex; flex-direction: column; align-items: center; gap: 4px;
  }
  .ash-bar {
    width: 100%; background: linear-gradient(180deg, #f90, #ff6600);
    border-radius: 6px 6px 0 0; transition: filter .2s;
    position: relative; cursor: pointer;
    min-height: 4px;
    box-shadow: 0 2px 8px rgba(255,153,0,0.25);
  }
  .ash-bar:hover { filter: brightness(1.1); }
  .ash-bar-val {
    position: absolute; top: -18px; left: 50%; transform: translateX(-50%);
    font-size: 9.5px; font-weight: 700; color: #92400e; white-space: nowrap;
  }
  .ash-bar-label { font-size: 10px; color: #94a3b8; font-weight: 600; }

  /* TOP LIST */
  .ash-top-list { list-style: none; display: grid; gap: 8px; }
  .ash-top-list li {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 12px; background: #f8fafc; border: 1px solid rgba(0,0,0,0.04);
    border-radius: 12px; cursor: pointer; transition: all .2s;
  }
  .ash-top-list li:hover { background: #fff5e6; border-color: rgba(255,153,0,0.30); transform: translateX(2px); }
  .ash-top-rank {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; flex-shrink: 0;
    background: #e2e8f0; color: #64748b;
  }
  .ash-top-rank.rank-1 { background: linear-gradient(135deg, #fbbf24, #d97706); color: #fff; box-shadow: 0 3px 8px rgba(217,119,6,0.30); }
  .ash-top-rank.rank-2 { background: linear-gradient(135deg, #cbd5e1, #64748b); color: #fff; }
  .ash-top-rank.rank-3 { background: linear-gradient(135deg, #fb923c, #c2410c); color: #fff; }
  .ash-top-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; overflow: hidden; }
  .ash-top-info strong { font-size: 13px; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ash-top-info span { font-size: 11px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ash-top-stats { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; flex-shrink: 0; }
  .ash-top-stats strong { font-size: 13px; color: #0f172a; }
  .ash-top-stats span { font-size: 10.5px; color: #94a3b8; }
  .ash-top-arrow { color: #cbd5e1; font-size: 12px; flex-shrink: 0; }

  /* TOOLBAR */
  .ash-toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
  .ash-search {
    position: relative; min-width: 280px;
  }
  .ash-search i {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    color: #94a3b8; font-size: 13px;
  }
  .ash-search input {
    width: 100%; height: 40px; padding: 0 14px 0 36px;
    border: 1.5px solid #e5e7eb; border-radius: 10px;
    font-size: 13px; font-family: inherit; color: #0f172a; background: #fff;
    outline: none; transition: all .2s;
  }
  .ash-search input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
  .ash-select {
    height: 40px; padding: 0 14px;
    border: 1.5px solid #e5e7eb; border-radius: 10px;
    font-size: 13px; font-family: inherit; color: #0f172a;
    background: #fff; cursor: pointer; outline: none;
    transition: all .2s;
  }
  .ash-select:focus { border-color: #f90; }

  /* TABLE */
  .ash-table-wrap { overflow-x: auto; }
  .ash-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
  .ash-table thead th {
    text-align: left; padding: 10px 14px;
    font-size: 11px; font-weight: 700; color: #64748b;
    text-transform: uppercase; letter-spacing: .5px;
    border-bottom: 2px solid #f1f5f9; background: #fafbfc;
    position: sticky; top: 0;
  }
  .ash-table thead th.num { text-align: right; }
  .ash-table tbody tr {
    cursor: pointer; transition: background .15s;
  }
  .ash-table tbody tr:hover { background: #fff5e6; }
  .ash-table tbody td {
    padding: 12px 14px; border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .ash-table tbody td.num { text-align: right; }
  .ash-table.compact thead th { padding: 8px 12px; font-size: 10.5px; }
  .ash-table.compact tbody td { padding: 8px 12px; }

  .ash-cell-shop {
    display: flex; gap: 12px; align-items: center;
  }
  .ash-cell-icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0;
  }
  .ash-cell-shop strong { display: block; font-size: 13px; color: #0f172a; }
  .ash-cell-shop span { font-size: 11.5px; color: #6b7280; }
  .ash-cell-shop em { color: #94a3b8; font-style: normal; }
  .ash-cell-tags { display: flex; gap: 4px; margin-top: 4px; }
  .ash-tag {
    font-size: 10px; padding: 2px 7px; border-radius: 6px;
    font-weight: 700; text-transform: uppercase; letter-spacing: .3px;
  }
  .ash-tag.green { background: #dcfce7; color: #15803d; }
  .ash-tag.gray { background: #f1f5f9; color: #64748b; }
  .ash-tag.orange { background: #fff5e6; color: #c2410c; }

  .ash-cell-orders strong { display: block; font-size: 14px; color: #0f172a; }
  .ash-cell-orders small { display: flex; gap: 4px; justify-content: flex-end; margin-top: 2px; }
  .ash-mini-pill {
    font-size: 10px; padding: 1px 6px; border-radius: 5px; font-weight: 700;
  }
  .ash-mini-pill.green { background: #dcfce7; color: #15803d; }
  .ash-mini-pill.yellow { background: #fef3c7; color: #92400e; }
  .ash-mini-pill.red { background: #fee2e2; color: #b91c1c; }

  .ash-cell-time strong { display: block; font-size: 12.5px; color: #0f172a; }
  .ash-cell-time small { font-size: 10.5px; color: #94a3b8; }

  /* PAGINATION */
  .ash-pagination {
    display: flex; justify-content: center; align-items: center; gap: 14px;
    padding-top: 16px;
  }
  .ash-pagination button {
    padding: 8px 14px; background: #fff; border: 1.5px solid #e5e7eb;
    border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 600;
    color: #475569; transition: all .2s; font-family: inherit;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .ash-pagination button:hover:not(:disabled) { border-color: #f90; color: #f90; background: #fff5e6; }
  .ash-pagination button:disabled { opacity: .4; cursor: not-allowed; }
  .ash-pagination span { font-size: 13px; color: #64748b; font-weight: 600; }

  /* LOADING + EMPTY */
  .ash-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 50px 20px; gap: 16px; color: #64748b;
  }
  .ash-spinner {
    width: 36px; height: 36px; border-radius: 50%;
    border: 3px solid #f0f0f0; border-top-color: #f90;
    animation: ashSpin .8s linear infinite;
  }
  @keyframes ashSpin { to { transform: rotate(360deg); } }

  .ash-empty {
    text-align: center; padding: 50px 20px; color: #6b7280;
  }
  .ash-empty i { font-size: 44px; color: #d4d4d8; margin-bottom: 14px; display: block; }
  .ash-empty h3 { font-size: 16px; color: #0f172a; margin-bottom: 6px; }
  .ash-empty p { font-size: 13px; color: #6b7280; }
  .ash-empty-mini {
    text-align: center; padding: 30px 20px; color: #94a3b8;
  }
  .ash-empty-mini i { font-size: 30px; color: #d4d4d8; margin-bottom: 8px; display: block; }
  .ash-empty-mini p { font-size: 12.5px; }

  /* ALERT */
  .ash-alert {
    display: flex; align-items: center; gap: 10px;
    border-radius: 12px; padding: 12px 16px; font-size: 13px; margin: 8px 0;
  }
  .ash-alert.error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

  /* DRAWER */
  .ash-drawer-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(15,23,42,0.5); backdrop-filter: blur(2px);
    display: flex; justify-content: flex-end;
    animation: ashFadeIn .2s ease;
  }
  @keyframes ashFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .ash-drawer {
    width: 100%; max-width: 760px; height: 100vh;
    background: #fff; box-shadow: -12px 0 50px rgba(0,0,0,0.25);
    overflow-y: auto; display: flex; flex-direction: column;
    animation: ashSlideIn .25s ease;
  }
  @keyframes ashSlideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

  .ash-drawer-head {
    position: sticky; top: 0; background: #fff; z-index: 5;
    display: flex; justify-content: space-between; align-items: center; gap: 14px;
    padding: 18px 22px; border-bottom: 1px solid #f1f5f9;
  }
  .ash-drawer-title { display: flex; gap: 14px; align-items: center; min-width: 0; flex: 1; }
  .ash-drawer-icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: linear-gradient(135deg, #95BF47, #5E8E3E);
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-size: 20px; flex-shrink: 0;
  }
  .ash-drawer-title strong { display: block; font-size: 16px; color: #0f172a; }
  .ash-drawer-title span { font-size: 12px; color: #6b7280; }
  .ash-drawer-title em { color: #94a3b8; font-style: normal; }
  .ash-drawer-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
  .ash-drawer-actions .ash-btn-ghost {
    background: #fff; color: #475569; border-color: #e5e7eb;
  }
  .ash-drawer-actions .ash-btn-ghost:hover { background: #fff5e6; border-color: #f90; color: #f90; }
  .ash-drawer-close {
    width: 36px; height: 36px; border-radius: 10px;
    background: #f8f9fa; border: 1px solid #e5e7eb; color: #6b7280;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all .2s; font-family: inherit;
  }
  .ash-drawer-close:hover { background: #fee2e2; border-color: #fca5a5; color: #ef4444; }

  .ash-drawer-body {
    padding: 20px 22px; display: grid; gap: 22px;
  }

  /* METRICS */
  .ash-metrics {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px;
  }
  .ash-metric {
    background: #f8fafc; border: 1px solid rgba(0,0,0,0.04);
    border-radius: 12px; padding: 12px 14px;
    display: flex; flex-direction: column; gap: 4px;
  }
  .ash-metric.highlight {
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    border-color: rgba(255,153,0,0.30);
  }
  .ash-metric span { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; }
  .ash-metric strong { font-size: 17px; color: #0f172a; font-weight: 800; letter-spacing: -0.3px; }

  /* SELETOR DE LOJAS NO DRAWER (multi-loja por cliente) */
  .ash-store-tabs {
    display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px;
  }
  .ash-store-tab {
    display: flex; align-items: center; gap: 6px; padding: 7px 12px;
    border-radius: 9px; border: 1px solid rgba(0,0,0,0.1); background: #fff;
    cursor: pointer; font-size: 12.5px; font-family: 'Lexend', sans-serif;
  }
  .ash-store-tab.is-active { background: #ff9900; color: #fff; border-color: #ff9900; font-weight: 600; }

  /* STATUS PILLS */
  .ash-status-row {
    display: flex; gap: 8px; flex-wrap: wrap;
  }
  .ash-pill {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 12px; padding: 6px 12px; border-radius: 999px;
    font-weight: 700;
  }
  .ash-pill.green { background: #dcfce7; color: #15803d; }
  .ash-pill.yellow { background: #fef3c7; color: #92400e; }
  .ash-pill.red { background: #fee2e2; color: #b91c1c; }
  .ash-pill.blue { background: #dbeafe; color: #1e40af; }

  .ash-pill-sm {
    display: inline-flex; align-items: center; padding: 2px 8px;
    border-radius: 999px; font-size: 10px; font-weight: 800;
    text-transform: uppercase; letter-spacing: .3px;
  }
  .ash-pill-sm.green { background: #dcfce7; color: #15803d; }
  .ash-pill-sm.yellow { background: #fef3c7; color: #92400e; }
  .ash-pill-sm.red { background: #fee2e2; color: #b91c1c; }

  /* SECTION */
  .ash-section h3 {
    font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 12px;
    display: flex; align-items: center; gap: 8px;
  }
  .ash-section h3 i { color: #f90; font-size: 14px; }

  .ash-config {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 8px 14px;
  }
  .ash-config > div {
    display: flex; flex-direction: column; gap: 2px;
    padding: 8px 12px; background: #f8fafc; border-radius: 8px;
    border: 1px solid rgba(0,0,0,0.03);
  }
  .ash-config dt { font-size: 10.5px; color: #6b7280; text-transform: uppercase; letter-spacing: .4px; font-weight: 700; }
  .ash-config dd { font-size: 13px; color: #0f172a; word-break: break-word; font-weight: 500; }
  .ash-config dd em { color: #94a3b8; font-style: italic; font-weight: 400; }
  .ash-config dd code {
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 12px;
    background: #fff; color: #c2410c; padding: 2px 6px; border-radius: 4px;
    border: 1px solid #fef3c7;
  }
  .ash-config-url {
    font-size: 11.5px !important;
    word-break: break-all; color: #6b7280 !important;
    font-family: 'JetBrains Mono', 'Courier New', monospace !important;
  }

  /* ORDERS */
  .ash-orders-list { display: grid; gap: 8px; }
  .ash-order {
    display: flex; gap: 14px; padding: 12px 14px;
    background: #f8fafc; border: 1px solid rgba(0,0,0,0.04);
    border-radius: 12px;
  }
  .ash-order-success { border-left: 3px solid #22c55e; }
  .ash-order-partial { border-left: 3px solid #f59e0b; }
  .ash-order-failed { border-left: 3px solid #ef4444; }
  .ash-order-main { flex: 1; min-width: 0; display: grid; gap: 4px; }
  .ash-order-head { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .ash-order-head strong { font-size: 13px; color: #0f172a; }
  .ash-order-meta {
    display: flex; gap: 12px; flex-wrap: wrap;
    font-size: 11.5px; color: #6b7280;
  }
  .ash-order-meta i { color: #94a3b8; margin-right: 4px; font-size: 10px; }
  .ash-order-dest { font-size: 11.5px; color: #6b7280; line-height: 1.4; }
  .ash-order-dest i { color: #f90; margin-right: 4px; }
  .ash-order-error {
    margin-top: 4px; padding: 6px 10px;
    background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 6px; color: #b91c1c; font-size: 11.5px;
    display: flex; align-items: center; gap: 6px;
  }
  .ash-order-side { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
  .ash-order-side strong { font-size: 14px; color: #0f172a; }
  .ash-order-side small { font-size: 10px; color: #94a3b8; }
  .ash-tracking {
    font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 11px;
    color: #15803d; background: #f0fdf4; padding: 1px 7px; border-radius: 4px;
    border: 1px solid #bbf7d0;
  }

  /* RESPONSIVE */
  /* Monitor estreito / vertical (~1080px): empilha pra aproveitar a altura */
  @media (max-width: 1280px) {
    .ash-grid-2 { grid-template-columns: 1fr; }
  }
  @media (max-width: 720px) {
    .ash-header-inner { padding: 12px 16px; flex-wrap: wrap; }
    .ash-title { font-size: 17px; }
    .ash-subtitle { display: none; }
    .ash-main { padding: 18px 14px 50px; }
    .ash-card { padding: 18px; border-radius: 14px; }
    .ash-card-head { flex-direction: column; align-items: stretch; }
    .ash-toolbar { width: 100%; }
    .ash-search { min-width: 0; flex: 1; }
    .ash-table { font-size: 12px; }
    .ash-table thead th, .ash-table tbody td { padding: 8px 10px; }
    .ash-cell-icon { display: none; }
    .ash-drawer { max-width: 100%; }
    .ash-drawer-body { padding: 16px; }
  }
`;

export default AdminShopify;
